/**
 * Módulo de síntese sonora procedimental e feedback háptico para operações de resgate.
 *
 * Utiliza exclusivamente a Web Audio API nativa:
 * - 0 arquivos externos de áudio (sem requisições HTTP adicionais ou falhas de rede)
 * - Baixíssima latência (geração em tempo real via osciladores e envelopes)
 * - Inicialização tardia (respeita a política de autoplay de navegadores)
 * - Suporte a vibração háptica no mobile (navigator.vibrate)
 */

let audioCtx: AudioContext | null = null
let mutado = false

function obterAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (mutado) return null

  if (!audioCtx) {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (AudioContextClass) {
      try {
        audioCtx = new AudioContextClass()
      } catch {
        return null
      }
    }
  }

  if (audioCtx && audioCtx.state === 'suspended') {
    void audioCtx.resume()
  }

  return audioCtx
}

/**
 * Alterna o estado de silenciamento dos efeitos sonoros.
 */
export function alternarMudoSom(): boolean {
  mutado = !mutado
  return mutado
}

export function estaMutadoSom(): boolean {
  return mutado
}

export function definirMutadoSom(valor: boolean): void {
  mutado = valor
}

/**
 * Emite vibração háptica em dispositivos móveis compatíveis.
 */
export function dispararVibracaoTatil(padraoMs: number | number[] = 35): void {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      navigator.vibrate(padraoMs)
    } catch {
      // Ignora erro em ambientes restritos de iframe
    }
  }
}

/**
 * Som de estalo e cisalhamento de aço/metal (corte de dobradiças, travas, colunas).
 */
export function tocarSomCorteMetal(): void {
  dispararVibracaoTatil([30, 20, 45])
  const ctx = obterAudioContext()
  if (!ctx) return

  const agora = ctx.currentTime

  // 1. Estalo de alta frequência (snap metálico)
  const oscSnap = ctx.createOscillator()
  const ganhoSnap = ctx.createGain()
  oscSnap.type = 'triangle'
  oscSnap.frequency.setValueAtTime(880, agora)
  oscSnap.frequency.exponentialRampToValueAtTime(140, agora + 0.12)

  ganhoSnap.gain.setValueAtTime(0.3, agora)
  ganhoSnap.gain.exponentialRampToValueAtTime(0.001, agora + 0.14)

  oscSnap.connect(ganhoSnap)
  ganhoSnap.connect(ctx.destination)
  oscSnap.start(agora)
  oscSnap.stop(agora + 0.15)

  // 2. Ruído de atrito e raspagem de lâmina
  const bufferSize = ctx.sampleRate * 0.1
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
  const dados = buffer.getChannelData(0)
  for (let i = 0; i < bufferSize; i += 1) {
    dados[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.3))
  }

  const ruidoNode = ctx.createBufferSource()
  ruidoNode.buffer = buffer

  const filtro = ctx.createBiquadFilter()
  filtro.type = 'bandpass'
  filtro.frequency.setValueAtTime(1800, agora)
  filtro.Q.setValueAtTime(4, agora)

  const ganhoRuido = ctx.createGain()
  ganhoRuido.gain.setValueAtTime(0.25, agora)
  ganhoRuido.gain.exponentialRampToValueAtTime(0.001, agora + 0.1)

  ruidoNode.connect(filtro)
  filtro.connect(ganhoRuido)
  ganhoRuido.connect(ctx.destination)

  ruidoNode.start(agora)
}

/**
 * Som de quebra/estilhaçamento de vidro temperado ou perfuração com punção.
 */
export function tocarSomEstilhacoVidro(): void {
  dispararVibracaoTatil([20, 15, 20])
  const ctx = obterAudioContext()
  if (!ctx) return

  const agora = ctx.currentTime

  // Rajada granular de alta frequência
  const duracao = 0.22
  const bufferSize = Math.floor(ctx.sampleRate * duracao)
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
  const dados = buffer.getChannelData(0)
  for (let i = 0; i < bufferSize; i += 1) {
    dados[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 2)
  }

  const noise = ctx.createBufferSource()
  noise.buffer = buffer

  const filtro = ctx.createBiquadFilter()
  filtro.type = 'highpass'
  filtro.frequency.setValueAtTime(2600, agora)

  const ganho = ctx.createGain()
  ganho.gain.setValueAtTime(0.4, agora)
  ganho.gain.exponentialRampToValueAtTime(0.001, agora + duracao)

  noise.connect(filtro)
  filtro.connect(ganho)
  ganho.connect(ctx.destination)

  noise.start(agora)
}

/**
 * Som de impacto seco e transferência de energia cinética veicular.
 */
export function tocarSomImpacto(): void {
  dispararVibracaoTatil([60, 30, 80])
  const ctx = obterAudioContext()
  if (!ctx) return

  const agora = ctx.currentTime

  // Tom grave de pancada no chassi
  const osc = ctx.createOscillator()
  const ganho = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(140, agora)
  osc.frequency.exponentialRampToValueAtTime(32, agora + 0.35)

  ganho.gain.setValueAtTime(0.6, agora)
  ganho.gain.exponentialRampToValueAtTime(0.001, agora + 0.4)

  osc.connect(ganho)
  ganho.connect(ctx.destination)
  osc.start(agora)
  osc.stop(agora + 0.42)
}
